import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  ISupplierRepository,
  Supplier,
  SupplierFilters,
  CreateSupplierInput,
  UpdateSupplierInput,
} from '../../../interfaces/src';
import { consumeEntityCode, peekEntityCode } from './entity-codes';

export class SupabaseSupplierRepository implements ISupplierRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async peekNextCode(): Promise<string> {
    return peekEntityCode(this.supabase, 'supplier');
  }

  async list(filters?: SupplierFilters): Promise<Supplier[]> {
    let query = this.supabase.from('suppliers').select('*');
    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }
    if (filters?.type) {
      query = query.eq('type', filters.type);
    }
    const { data, error } = await query.order('name');
    if (error) throw error;
    return (data ?? []).map(mapSupplier);
  }

  async getById(id: string): Promise<Supplier | null> {
    const { data, error } = await this.supabase.from('suppliers').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapSupplier(data);
  }

  async create(input: CreateSupplierInput): Promise<Supplier> {
    const code = input.code && input.code.trim() !== ''
      ? input.code.trim()
      : await consumeEntityCode(this.supabase, 'supplier');
    const { data, error } = await this.supabase
      .from('suppliers')
      .insert({
        code,
        name: input.name,
        type: input.type,
        tax_id: input.taxId,
        address: input.address,
        city: input.city,
        phone: input.phone,
        fax: input.fax,
        email: input.email,
        notes: input.notes,
      })
      .select()
      .single();
    if (error) throw error;
    return mapSupplier(data);
  }

  async update(id: string, input: UpdateSupplierInput): Promise<Supplier> {
    const mapped: TablesUpdate<'suppliers'> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.type !== undefined) mapped['type'] = input.type;
    if (input.taxId !== undefined) mapped['tax_id'] = input.taxId;
    if (input.address !== undefined) mapped['address'] = input.address;
    if (input.city !== undefined) mapped['city'] = input.city;
    if (input.phone !== undefined) mapped['phone'] = input.phone;
    if (input.fax !== undefined) (mapped as Record<string, unknown>)['fax'] = input.fax;
    if (input.email !== undefined) mapped['email'] = input.email;
    if (input.notes !== undefined) mapped['notes'] = input.notes;

    const { data, error } = await this.supabase
      .from('suppliers')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapSupplier(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('suppliers').delete().eq('id', id);
    if (error) throw error;
  }
}

function mapSupplier(row: Record<string, unknown>): Supplier {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    type: row['type'] as Supplier['type'],
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
